import axios from "axios";
import {API_URL, base64ToBlob} from "../config";
import {
    showStatisticsExportLoader,
    showStatisticsLoader,
    updateStatisticsSortFields
} from "../actions/actionsStatisticsList";
import {
    GET_FILES_HISTORY,
    GET_STATISTIC,
    GET_STATISTICS, GET_STATISTICS_FILES_PAGINATION,
    GET_STATISTICS_PAGINATION,
    GET_STATISTICS_USER_SORT_FIELDS, SET_FILES_HISTORY_LOADER,
    SET_RATED_QUESTIONNAIRES,
    SHOW_DETAIL_LOADING,
    GET_STATISTIC_DASHBOARD,
    SET_STATISTIC_DASHBOARD_LOADING
} from "../types/typesStatisticsList";
import {showAccessDeniedNotifications} from "../actions/actionsNotifications";
import {GET_NOTIFICATION_ERROR} from "../types/types";
import {setUserError} from "../actions/actionsUsersList";
import {SET_USER_STATUS_ERROR, SET_USER_STATUS_SUCCESS} from "../types/typesUsersList";
import {hideError, hideErrorMessage} from "../actions/actions";
import {statisticsSortFieldsFetch, withStatisticsBranchIdSortField} from "./sortFieldsFetchState";
import {fetchFormsByCallDetailId} from "./apiQualityControlStatistics";
import {normalizeSimpleUsersControls} from "../../components/Statistics/ManualEvaluationDialog/manualEvaluationQuestionnaires";

export function getStatisticsWithPaginationFilterAndSorting(data: any, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch(showStatisticsLoader(true))
        axios.post(`${API_URL}Statistic/GetStatistic`, data, {
            headers: {
                'Content-Type': 'application/json',
                "Authorization": `Bearer ${jwtToken}`
            }
        })
            .then((response) => {
                dispatch({type: GET_STATISTICS, payload: response.data.data.items})
                dispatch({type: GET_STATISTICS_PAGINATION, payload: response.data.data})
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: true});
                    return dispatch(setUserError(true))
                }
            })
            .finally(() => {
                dispatch(showAccessDeniedNotifications(false));
                dispatch(showStatisticsLoader(false))
            })
    }
}

const pickDashboardProp = (obj: any, camel: string) => {
    if (!obj) return undefined;
    const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
    return obj[camel] ?? obj[pascal];
};

const normalizeDashboardItem = (item: any) => ({
    callGroup: pickDashboardProp(item, 'callGroup') || '',
    total: pickDashboardProp(item, 'total') ?? 0,
    active: pickDashboardProp(item, 'active') ?? 0,
    success: pickDashboardProp(item, 'success') ?? 0,
    unsuccess: pickDashboardProp(item, 'unsuccess') ?? 0,
});

const normalizeDashboardList = (list: any) => {
    const items = Array.isArray(list) ? list : list && typeof list === 'object' ? Object.values(list) : [];
    return items.map(normalizeDashboardItem);
};

const normalizeStatisticDashboard = (payload: any) => {
    if (!payload || typeof payload !== 'object') return null;
    const inner = (payload.campaigns || payload.Campaigns || payload.internalTotal || payload.InternalTotal
        || payload.externalTotal || payload.ExternalTotal || payload.campaignsTotal || payload.CampaignsTotal)
        ? payload
        : payload.data;
    if (!inner || typeof inner !== 'object') return null;
    return {
        campaigns: normalizeDashboardList(pickDashboardProp(inner, 'campaigns')),
        campaignsTotal: pickDashboardProp(inner, 'campaignsTotal')
            ? normalizeDashboardItem(pickDashboardProp(inner, 'campaignsTotal'))
            : null,
        internal: normalizeDashboardList(pickDashboardProp(inner, 'internal')),
        internalTotal: pickDashboardProp(inner, 'internalTotal')
            ? normalizeDashboardItem(pickDashboardProp(inner, 'internalTotal'))
            : null,
        external: normalizeDashboardList(pickDashboardProp(inner, 'external')),
        externalTotal: pickDashboardProp(inner, 'externalTotal')
            ? normalizeDashboardItem(pickDashboardProp(inner, 'externalTotal'))
            : null,
        total: pickDashboardProp(inner, 'total')
            ? normalizeDashboardItem(pickDashboardProp(inner, 'total'))
            : null,
    };
};

let statisticDashboardRequestId = 0;

export function getStatisticDashboard(filter: {key: string; value: string}[], jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        const requestId = ++statisticDashboardRequestId;
        dispatch({type: SET_STATISTIC_DASHBOARD_LOADING, payload: true});
        axios.post(`${API_URL}Statistic/GetStatisticDashboard`, filter || [], {
            headers: {
                'Content-Type': 'application/json',
                "Authorization": `Bearer ${jwtToken}`
            }
        })
            .then((response) => {
                if (requestId !== statisticDashboardRequestId) return;
                const raw = response?.data?.data ?? response?.data;
                dispatch({type: GET_STATISTIC_DASHBOARD, payload: normalizeStatisticDashboard(raw)})
            })
            .catch((e) => {
                if (requestId !== statisticDashboardRequestId) return;
                dispatch({type: GET_STATISTIC_DASHBOARD, payload: null});
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                }
            })
            .finally(() => {
                if (requestId === statisticDashboardRequestId) {
                    dispatch({type: SET_STATISTIC_DASHBOARD_LOADING, payload: false});
                }
                dispatch(showAccessDeniedNotifications(false));
            });
    }
}

export function clearStatisticDashboard() {
    return (dispatch: (arg0: any) => void) => {
        statisticDashboardRequestId += 1;
        dispatch({type: GET_STATISTIC_DASHBOARD, payload: null});
        dispatch({type: SET_STATISTIC_DASHBOARD_LOADING, payload: false});
    };
}

export function getStatisticsUserSortFields(jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        if (!jwtToken) {
            return;
        }
        if (statisticsSortFieldsFetch.requestInFlight) {
            return statisticsSortFieldsFetch.requestInFlight;
        }
        if (statisticsSortFieldsFetch.lastAttemptToken === jwtToken) {
            return;
        }

        statisticsSortFieldsFetch.lastAttemptToken = jwtToken;
        statisticsSortFieldsFetch.requestInFlight = axios.get(`${API_URL}Statistic/GetUserSortFields`, {
            headers: {
                'Content-Type': 'application/json',
                "Authorization": `Bearer ${jwtToken}`
            }
        })
            .then((response) => {
                const sortFieldsData = withStatisticsBranchIdSortField(response.data.data);
                dispatch({type: GET_STATISTICS_USER_SORT_FIELDS, payload: sortFieldsData});
                localStorage.setItem('callcenter:user_statistic_sort_fields', JSON.stringify(sortFieldsData?.fields));
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    return dispatch({type: SET_USER_STATUS_ERROR, payload: {message: 'Помилка завантаження даних'}})
                }
            })
            .finally(() => {
                statisticsSortFieldsFetch.requestInFlight = null;
                dispatch(showAccessDeniedNotifications(false));
            });

        return statisticsSortFieldsFetch.requestInFlight;
    }
}

export function getEventStatisticToUserDetail(id: any, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch({type: SHOW_DETAIL_LOADING, payload: true})
        dispatch({type: GET_NOTIFICATION_ERROR, payload: false});
        dispatch({type: SET_RATED_QUESTIONNAIRES, payload: []});

        Promise.all([
            axios.get(`${API_URL}Statistic/GetStatisticCallDetailById?callId=${id}`, {
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${jwtToken}`
                }
            }),
            fetchFormsByCallDetailId(id, true, jwtToken).catch(() => null),
        ])
            .then(([detailResponse, ratedFormsResponse]) => {
                dispatch({type: GET_STATISTIC, payload: detailResponse.data.data});

                const ratedQuestionnaires = ratedFormsResponse?.data?.statusCode === 200
                    ? normalizeSimpleUsersControls(ratedFormsResponse.data.data, {isRated: true})
                    : [];
                dispatch({type: SET_RATED_QUESTIONNAIRES, payload: ratedQuestionnaires});
            })
            .catch(() => {
                dispatch({type: SHOW_DETAIL_LOADING, payload: false})
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    dispatch({type: SHOW_DETAIL_LOADING, payload: false})
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: true});
                    if (e.data) {
                        dispatch({type: SET_USER_STATUS_ERROR, payload: {message: e.data.message}})
                    } else {
                        dispatch({type: SET_USER_STATUS_ERROR, payload: {message: 'Щось пішло не так.'}})
                    }
                }
            })
            .finally(() => {
                    dispatch(showAccessDeniedNotifications(false));
                    dispatch({type: SHOW_DETAIL_LOADING, payload: false});
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: false});
                    dispatch({type: SET_USER_STATUS_ERROR, payload: false})
                }
            )
    }
}

export function getStatisticExcel(dataSort: any, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch(hideError())
        dispatch(hideErrorMessage())
        dispatch(showStatisticsExportLoader(true));
        axios.post(`${API_URL}Statistic/GetStatisticExcelWithData`, dataSort, {
            headers: {
                'Content-Type': 'application/json',
                "Authorization": `Bearer ${jwtToken}`
            },
            responseType: 'json'
        })
            .then((response) => {
                const fileName = response.data.data.fileDownloadName;
                const fileContentsBase64 = response.data.data.fileContents;

                const blob = base64ToBlob(fileContentsBase64, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                window.URL.revokeObjectURL(url);
                link.remove();
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    return dispatch(setUserError(true))
                }
            })
            .finally(() => {
                dispatch(showStatisticsExportLoader(false));
                dispatch(showAccessDeniedNotifications(false));
                return dispatch(setUserError(false))
            })
    }
}

export function updateUserStatisticFields(sortFields: any, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        axios.post(`${API_URL}Statistic/UpdateUserSortFields`, sortFields, {
            headers: {
                'Content-Type': 'application/json',
                "Authorization": `Bearer ${jwtToken}`
            }
        })
            .then((response) => {
                const fields = withStatisticsBranchIdSortField({fields: response.data.data?.fields}).fields;
                dispatch(updateStatisticsSortFields(fields));
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    return dispatch({type: SET_USER_STATUS_ERROR, payload: {message: 'Помилка оновлення стовпців'}})
                }
            })
            .finally(() => {
                dispatch(showAccessDeniedNotifications(false));
                dispatch({type: SET_USER_STATUS_ERROR, payload: null});
            })
    }
}

export function getFilesHistory (dataSort: any, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch({type: SET_FILES_HISTORY_LOADER, payload: true})
        dispatch({type: GET_NOTIFICATION_ERROR, payload: false});
        axios.post(`${API_URL}Statistic/GetFilesHistory`, dataSort,{
            headers: {
                'Content-Type': 'application/json',
                "Authorization": `Bearer ${jwtToken}`
            }
        })
            .then((response) => {
                dispatch({type: GET_FILES_HISTORY, payload: response.data.data.items});
                dispatch({type: GET_STATISTICS_FILES_PAGINATION, payload: response.data.data});
            })
            .catch(() => {
                dispatch({type: SET_FILES_HISTORY_LOADER, payload: false})
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    dispatch({type: SET_FILES_HISTORY_LOADER, payload: false});
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: true});
                    if (e.data) {
                        dispatch({type: SET_USER_STATUS_ERROR, payload: {message: e.data.message}})
                    } else {
                        dispatch({type: SET_USER_STATUS_ERROR, payload: {message: 'Щось пішло не так.'}})
                    }
                }
            })
            .finally(() => {
                    dispatch(showAccessDeniedNotifications(false));
                    dispatch({type: SET_FILES_HISTORY_LOADER, payload: false});
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: false});
                    dispatch({type: SET_USER_STATUS_ERROR, payload: false})
                }
            )
    }
}


export function generateStatisticByType(dataSort: any, type: number | any, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch(hideError())
        dispatch(hideErrorMessage())
        dispatch(showStatisticsExportLoader(true));
        axios.post(`${API_URL}Statistic/GenerateStatistic?type=${type}`, dataSort, {
            headers: {
                'Content-Type': 'application/json',
                "Authorization": `Bearer ${jwtToken}`
            },
            responseType: 'json'
        })
            .then((response) => {
                dispatch({type: SET_USER_STATUS_SUCCESS, payload: {message: response.data.message}})
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    return dispatch(setUserError(true))
                }
                dispatch(showStatisticsExportLoader(false));
            })
            .finally(() => {
                dispatch(showAccessDeniedNotifications(false));
                dispatch({type: SET_USER_STATUS_SUCCESS, payload: null})
                return dispatch(setUserError(false));
            })
    }
}

export function getStatisticExcelByLink(id: any, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch(hideError());
        dispatch(hideErrorMessage());
        dispatch(showStatisticsExportLoader(true));

        axios.get(`${API_URL}Statistic/GetTempUrlFile?id=${id}`, {
            headers: {
                'Content-Type': 'application/json',
                "Authorization": `Bearer ${jwtToken}`
            },
            responseType: 'json'
        })
            .then((response) => {
                const fileUrl = response.data.data;
                const fileName = response.data.title || 'export.xlsx';

                if (fileUrl) {
                    const link = document.createElement('a');
                    link.href = fileUrl;
                    link.setAttribute('download', fileName);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                } else {
                    dispatch(setUserError(true));
                }
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    dispatch(setUserError(true));
                }
            })
            .finally(() => {
                dispatch(showStatisticsExportLoader(false));
                dispatch(showAccessDeniedNotifications(false));
                dispatch(setUserError(false));
            });
    }
}

export function deleteFileById(id: number | any, dataSort: any, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch(hideError())
        dispatch(hideErrorMessage())
        dispatch(showStatisticsExportLoader(true));
        axios.delete(`${API_URL}Statistic/DeleteFileById?id=${id}`, {
            headers: {
                'Content-Type': 'application/json',
                "Authorization": `Bearer ${jwtToken}`
            },
            responseType: 'json'
        })
            .then((response) => {
                dispatch({type: SET_USER_STATUS_SUCCESS, payload: {message: response.data.message}});
                dispatch(getFilesHistory(dataSort, jwtToken));
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    return dispatch(setUserError(true))
                }
            })
            .finally(() => {
                dispatch(showStatisticsExportLoader(false));
                dispatch(showAccessDeniedNotifications(false));
                return dispatch(setUserError(false))
            })
    }
}

export function deleteAllFiles(jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch(hideError())
        dispatch(hideErrorMessage())
        dispatch(showStatisticsExportLoader(true));
        axios.delete(`${API_URL}Statistic/DeleteAllFiles`, {
            headers: {
                'Content-Type': 'application/json',
                "Authorization": `Bearer ${jwtToken}`
            },
            responseType: 'json'
        })
            .then((response) => {
                dispatch({type: SET_USER_STATUS_SUCCESS, payload: {message: response.data.message}});
                dispatch({type: GET_FILES_HISTORY, payload: null});
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    return dispatch(setUserError(true))
                }
            })
            .finally(() => {
                dispatch(showStatisticsExportLoader(false));
                dispatch(showAccessDeniedNotifications(false));
                return dispatch(setUserError(false))
            })
    }
}
